// =============================================================================
// bannerAudience.ts — banner audience options, normalisation + who-sees-it match
// =============================================================================
// A banner's `audience` is a set of tokens (db-186 made the column text[]); a
// banner shows if ANY of its tokens applies to the viewer. This module is the ONE
// source for the option list (admin form + list display) and the match logic
// (ActiveBanners). Pure + unit-tested.
//
// `bannerAudiences()` tolerates BOTH the new text[] and the legacy single-string /
// comma form, so the app reads correctly before AND after the migration.
// =============================================================================

import { isInternalUser, isResponderJob } from "../app/access";
import { ROLES } from "../utils/enums";

export interface BannerAudienceOption {
  value: string;
  label: string;
}

// The tickable audiences (order = display order). "all" wins over everything.
export const BANNER_AUDIENCES: BannerAudienceOption[] = [
  { value: "all", label: "All users" },
  { value: "shelter", label: "DFV Services only" },
  { value: "staff", label: "Responders only" },
  { value: "combined", label: "Zephyr Combined" },
  { value: "picker", label: "Zephyr Pickers" },
  { value: "supervisor", label: "Zephyr Supervisors" },
  { value: "superuser", label: "Zephyr SuperUsers" },
  { value: "all_staff", label: "Zephyr All Staff" },
];

// Normalise a stored audience to a token array. Accepts text[] (new), a single
// string or a comma-joined string (legacy), or null.
export function bannerAudiences(audience: unknown): string[] {
  if (Array.isArray(audience)) return audience.map(String).filter(Boolean);
  if (typeof audience === "string" && audience.trim()) {
    return audience
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

// A human summary of a banner's audiences for the admin list.
export function bannerAudienceLabels(audience: unknown): string {
  const tokens = bannerAudiences(audience);
  if (tokens.length === 0 || tokens.includes("all")) return "All users";
  return tokens.map((v) => BANNER_AUDIENCES.find((o) => o.value === v)?.label || v).join(", ");
}

type AudienceUser = { role?: string | null; [k: string]: unknown };

// Does a single audience token apply to this user?
function tokenMatches(token: string, user: AudienceUser): boolean {
  switch (token) {
    case "all":
      return true;
    case "shelter":
      return user.role === ROLES.SHELTER;
    case "staff":
      return isResponderJob(user); // the existing "Responders only"
    case "combined":
      return user.role === ROLES.COMBINED;
    case "picker":
      return user.role === ROLES.PICKER;
    case "supervisor":
      // Supervisors = admins; SuperUsers outrank them, so include superuser too.
      return user.role === ROLES.ADMIN || user.role === ROLES.SUPERUSER;
    case "superuser":
      return user.role === ROLES.SUPERUSER;
    case "all_staff":
      return isInternalUser(user); // every Zephyr internal role
    default:
      return token === user.role; // legacy exact-role token
  }
}

// Does a banner's audience apply to this user (any token matches)?
export function bannerMatchesUser(
  audience: unknown,
  user: AudienceUser | null | undefined
): boolean {
  if (!user) return false;
  const tokens = bannerAudiences(audience);
  if (tokens.length === 0) return true; // no audience set = everyone (safe default)
  return tokens.some((t) => tokenMatches(t, user));
}
